export const updateStartDay = ({ commit }, startDay) => {
  commit("changeStartDay", startDay);
};

export const updateEndDay = ({ commit }, endDay) => {
  commit("changeEndDay", endDay);
};

export const updateCountry = ({ commit }, country) => {
  commit("changeCountry", country);
};

export const updateCountries = ({ commit }, countries) => {
  commit("changeCountries", countries);
};

export const updateTeam = ({ commit }, team) => {
  commit("changeTeam", team);
};

export const updateTeamName = ({ commit }, teamName) => {
  commit("changeTeamName", teamName);
};

export const updateTeamSlug = ({ commit }, teamSlug) => {
  commit("changeTeamSlug", teamSlug);
};

export const updateSuperteam = ({ commit }, superteam) => {
  commit("changeSuperteam", superteam);
};

export const updateSuperteamName = ({ commit }, superteamName) => {
  commit("changeSuperteamName", superteamName);
};

export const updateSuperteamSlug = ({ commit }, superteamSlug) => {
  commit("changeSuperteamSlug", superteamSlug);
};

// --- Composite scope transitions -------------------------------------------
// The superteam / team / individual dimensions have mutual-exclusion
// rules that are easy to get subtly wrong if scattered across components. Keep
// them here, in one place, so every entry point (search, leaderboard rows,
// filter chips) goes through the same transitions.
//
// Country and date are independent of every scope here: no action below touches
// either, so "this team's strides in Singapore this month" is a state the app can
// hold. The backend intersects them — `/dashboards/strides`, `strides_stats` and
// the leaderboard all take `country` alongside `team` / `superteam` / `user_id`.
// The mutual exclusion below is only between the scope dimensions themselves.
//
// State model — (superteam, team) encodes the team-side mode; an individual
// (userId) either stands alone or narrows a team scope it was picked from
// inside:
//   superteam=null, team=null  → global / country / date  (existing behaviour)
//   superteam=null, team=set   → standalone team          (existing behaviour)
//   superteam=set,  team=null  → superteam-only (aggregate of child teams)
//   superteam=set,  team=set   → drilled into one child team of the superteam
//   userId=set, no team side   → single individual, dashboard-wide
//   userId=set + a team scope  → that strider's strides within that team
// Which one a click produces is the caller's choice, not the state's: picking
// someone from the global Individuals list or from search is `selectUser` and
// drops the team side; picking a row out of a team's member list is
// `selectMember` and keeps it.

// Pick a superteam from the search or the Teams ranking: excludes a standalone
// team and any individual. Country / date stay, exactly as for a team.
// `slug` is the row's own URL slug where it has one; it only feeds the URL (see
// src/utils/urlFilters.js), so a caller without one may omit it.
export const selectSuperteam = ({ commit }, { id, name, slug = null }) => {
  commit("changeSuperteam", id);
  commit("changeSuperteamName", name);
  commit("changeSuperteamSlug", slug);
  commit("changeTeam", null);
  commit("changeTeamName", null);
  commit("changeTeamSlug", null);
  commit("changeUserId", null);
  commit("changeUserName", null);
};

// Pick a team from the search / Teams tab (standalone): clears any superteam
// scope and any individual. Country / date stay.
export const selectTeam = ({ commit }, { id, name, slug = null }) => {
  commit("changeTeam", id);
  commit("changeTeamName", name);
  commit("changeTeamSlug", slug);
  commit("changeSuperteam", null);
  commit("changeSuperteamName", null);
  commit("changeSuperteamSlug", null);
  commit("changeUserId", null);
  commit("changeUserName", null);
};

// Drill from a superteam into one of its child teams: keep the superteam parent,
// clear any individual.
export const drillTeam = ({ commit }, { id, name, slug = null }) => {
  commit("changeTeam", id);
  commit("changeTeamName", name);
  commit("changeTeamSlug", slug);
  commit("changeUserId", null);
  commit("changeUserName", null);
};

// Pick an individual from the Individuals tab: a scope mutually exclusive with
// both a team and a superteam, so clear the whole team side. Country / date stay.
export const selectUser = ({ commit }, { id, name }) => {
  commit("changeUserId", id);
  commit("changeUserName", name);
  commit("changeTeam", null);
  commit("changeTeamName", null);
  commit("changeTeamSlug", null);
  commit("changeSuperteam", null);
  commit("changeSuperteamName", null);
  commit("changeSuperteamSlug", null);
};

// Pick a member from inside a team scope: keeps the team/superteam it was picked
// from, so the dashboard stays in the Teams mode and the leaderboard keeps
// showing that team's members. The strides/stats requests carry both `team` and
// `user_id`, which the backend intersects.
export const selectMember = ({ commit }, { id, name }) => {
  commit("changeUserId", id);
  commit("changeUserName", name);
};

// Clear the superteam scope entirely — drops the drilled child too, back to global.
export const clearSuperteam = ({ commit }) => {
  commit("changeSuperteam", null);
  commit("changeSuperteamName", null);
  commit("changeSuperteamSlug", null);
  commit("changeTeam", null);
  commit("changeTeamName", null);
  commit("changeTeamSlug", null);
};

// Drop only the superteam half of the scope, from its filter chip. A child team
// drilled underneath it stays and becomes a standalone team scope — the same
// state as picking that team straight out of the Teams list. With no child
// drilled there's nothing to keep, so this lands on global like clearSuperteam.
export const detachSuperteam = ({ commit }) => {
  commit("changeSuperteam", null);
  commit("changeSuperteamName", null);
  commit("changeSuperteamSlug", null);
};

export const updateUserId = ({ commit }, userId) => {
  commit("changeUserId", userId);
};

export const updateUserName = ({ commit }, userName) => {
  commit("changeUserName", userName);
};

export const updateSelection = ({ commit }, selection) => {
  commit("changeSelection", selection);
};

// Raise the global snackbar (rendered once in App.vue). color/timeout are
// optional; the defaults suit a success toast.
export const showSnackbar = (
  { commit },
  { message, color = "success", timeout = 3000 }
) => {
  commit("changeSnackbar", { show: true, message, color, timeout });
};

export const updateTotalUsers = ({ commit }, users) => {
  commit("changeTotalUsers", users);
};

export const updateTotalCountries = ({ commit }, countries) => {
  commit("changeTotalCountries", countries);
};

export const updateStridesUsers = ({ commit }, users) => {
  commit("changeStridesUsers", users);
};

export const updateStridesCountries = ({ commit }, countries) => {
  commit("changeStridesCountries", countries);
};
